import { OrderExpiry, OrderType, RewardT } from '@/common/types'
import { create } from 'zustand'
import { SecurityOrderItem } from './types'
import Money from '@/common/models/money'

interface State {
    name?: string
    bundleId?: string
    orderType?: OrderType
    orderItems: SecurityOrderItem[]
    orderExpiry: OrderExpiry
    totalInvestmentAmount: number
    bundleRequestId: string | null
    hasFundsError: boolean
    isSubmitting: boolean
    promotions?: RewardT[]
}

interface BundleTradeState extends State {
    // Line management
    setOrderItems: (lines: Omit<SecurityOrderItem, 'requestId'>[]) => void
    addOrderItem: (line: Omit<SecurityOrderItem, 'requestId'>) => void
    updateOrderItem: (
        securityCode: string,
        updates: Partial<Omit<SecurityOrderItem, 'securityCode' | 'instrument' | 'requestId'>>,
    ) => void
    removeOrderItem: (securityCode: string) => void
    clearOrderItems: () => void

    // Global settings
    initiateBundleTrade: (name: string, bundleId: string) => void
    setOrderType: (type: OrderType) => void
    setOrderExpiry: (expiry: OrderExpiry) => void
    setTotalInvestmentAmount: (amount: number) => void
    setBundleRequestId: () => void

    setPromotions: (promotions: RewardT[]) => void

    // Error state
    hasFundsError: boolean
    setHasFundsError: (error: boolean) => void

    // submitting state
    isSubmitting: boolean
    setIsSubmitting: (submitting: boolean) => void

    // Reset
    resetState: () => void
}

const initialState: State = {
    orderItems: [],
    name: '',
    orderExpiry: 'VALID_FOR_DAYS_7',
    totalInvestmentAmount: 0,
    bundleId: undefined,
    bundleRequestId: crypto.randomUUID(),
    hasFundsError: false,
    isSubmitting: false,
    promotions: [],
}

const useBundleTradeStore = create<BundleTradeState>((set) => ({
    ...initialState,

    // Line management
    setOrderItems: (lines) =>
        set((s) => {
            const total = lines.reduce((acc, l) => acc + (l.amount ?? 0), 0)
            const orderItems = lines.map((line) => ({
                ...line,
                requestId:
                    s.orderItems.find((item) => item.securityCode === line.securityCode)?.requestId ??
                    crypto.randomUUID(),
                weight: total > 0 ? (line.amount ?? 0) / total : 1 / Math.max(lines.length, 1),
            }))

            return {
                orderItems,
            }
        }),
    addOrderItem: (line) =>
        set((s) => {
            const newList = [...s.orderItems, { ...line, requestId: crypto.randomUUID() }]
            const total = newList.reduce((acc, l) => acc + (l.amount ?? 0), 0)
            const normalized = newList.map((l) => ({
                ...l,
                weight: total > 0 ? (l.amount ?? 0) / total : 1 / Math.max(newList.length, 1),
            }))
            return { orderItems: normalized }
        }),
    updateOrderItem: (asxCode, updates) =>
        set((s) => {
            const updated = s.orderItems.map((line) => (line.securityCode === asxCode ? { ...line, ...updates } : line))
            const total = updated.reduce((acc, l) => acc + (l.amount ?? 0), 0)
            const normalized = updated.map((l) => ({
                ...l,
                weight: total > 0 ? (l.amount ?? 0) / total : 1 / Math.max(updated.length, 1),
            }))
            return { orderItems: normalized }
        }),
    removeOrderItem: (asxCode) =>
        set((s) => {
            const filtered = s.orderItems.filter((line) => line.securityCode !== asxCode)
            const total = filtered.reduce((acc, l) => acc + (l.amount ?? 0), 0)
            const normalized = filtered.map((l) => ({
                ...l,
                weight: total > 0 ? (l.amount ?? 0) / total : 1 / Math.max(filtered.length, 1),
            }))
            return { orderItems: normalized }
        }),
    clearOrderItems: () => set(() => ({ orderItems: [], totalInvestmentAmount: 0 })),

    // Global settings
    initiateBundleTrade: (name, bundleId) => set(() => ({ name, bundleId })),
    setOrderType: (type: OrderType) => set(() => ({ orderType: type })),
    setOrderExpiry: (expiry) => set(() => ({ orderExpiry: expiry })),
    setTotalInvestmentAmount: (amount) =>
        set(() => ({ totalInvestmentAmount: new Money(amount).valueWithPrecision(2) })),
    setBundleRequestId: () => set(() => ({ bundleRequestId: crypto.randomUUID() })),
    setPromotions: (promotions: RewardT[]) => set(() => ({ promotions })),

    // Error state
    setHasFundsError: (error: boolean) => set(() => ({ hasFundsError: error })),

    // submitting state
    isSubmitting: false,
    setIsSubmitting: (submitting: boolean) => set(() => ({ isSubmitting: submitting })),

    // Reset
    resetState: () =>
        set(() => ({
            ...initialState,
            bundleRequestId: crypto.randomUUID(),
        })),
}))

export default useBundleTradeStore
